import { Archive } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { POWER_LEVEL_ADMIN, useRoomSettings } from "~/hooks/useRoomSettings";
import { type RoomSummary } from "~/lib/matrix";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

const ROW_HEIGHT = 56;

export function ArchivedRoomsView() {
  const rooms = useMatrixRooms();
  const archivedRooms = useMemo(
    () =>
      rooms
        .filter((room) => !room.isSpace)
        .filter((room) => room.archived !== null)
        .sort((a, b) => b.lastActivityTs - a.lastActivityTs),
    [rooms],
  );

  return (
    <section
      aria-label="Archived channels"
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <header
        style={{
          height: "var(--chat-timeline-room-header-height)",
          display: "flex",
          alignItems: "center",
          padding: "0 var(--chat-space-4)",
          borderBottom: "1px solid var(--chat-surface-border)",
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 300,
          }}
        >
          Archived channels
        </h2>
      </header>
      {archivedRooms.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--chat-fg-secondary)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          No archived channels.
        </div>
      ) : (
        <div style={{ padding: "var(--chat-space-2) 0", overflowY: "auto" }}>
          {archivedRooms.map((room) => (
            <ArchivedRoomRow key={room.id} room={room} />
          ))}
        </div>
      )}
    </section>
  );
}

function ArchivedRoomRow({ room }: { room: RoomSummary }) {
  const { setActiveRoomId, client, userId } = useMatrix();
  const settings = useRoomSettings(room.id);
  const { push: pushToast } = useToast();
  const [saving, setSaving] = useState(false);
  const archivedAt = room.archived?.archivedAt ?? 0;
  const canUnarchive = useMemo(() => {
    if (!client || !userId) return false;
    const me = settings.getMembers().find((member) => member.userId === userId);
    return (me?.powerLevel ?? 0) >= POWER_LEVEL_ADMIN;
  }, [client, settings, userId]);

  const handleUnarchive = useCallback(async () => {
    setSaving(true);
    try {
      await settings.unarchive();
      pushToast({ message: "Unarchived", tone: "success" });
    } catch {
      pushToast({ message: "Could not unarchive. Try again.", tone: "danger" });
    } finally {
      setSaving(false);
    }
  }, [pushToast, settings]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        minHeight: ROW_HEIGHT,
        padding: "0 var(--chat-space-4)",
      }}
    >
      <button
        type="button"
        onClick={() => setActiveRoomId(room.id)}
        className="chat-button"
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          textAlign: "left",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <span style={{ color: "var(--chat-fg-muted)", display: "inline-flex", flexShrink: 0 }}>
          <Archive size={14} strokeWidth={ICON_STROKE} aria-hidden />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
            }}
          >
            #{room.name}
          </span>
          <span
            style={{
              display: "block",
              color: "var(--chat-fg-secondary)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            Archived{" "}
            {archivedAt > 0 ? (
              <RelativeLabel
                value={new Date(archivedAt).toISOString()}
                zone={LOCAL_ZONE}
                fallback={absoluteFallback(archivedAt)}
              />
            ) : (
              "just now"
            )}
          </span>
        </span>
      </button>
      {canUnarchive && (
        <button
          type="button"
          onClick={() => void handleUnarchive()}
          disabled={saving}
          className="chat-button"
          style={{
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.5 : 1,
            padding: 0,
          }}
        >
          Unarchive
        </button>
      )}
    </div>
  );
}
